<script setup>
import { nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';

const props = defineProps({
    placement: {
        type: String,
        default: 'right',
        validator: (value) => ['top', 'right', 'bottom', 'left'].includes(value)
    },
    offset: {
        type: Number,
        default: 12
    },
    open: {
        type: Boolean,
        default: undefined
    }
});

const emit = defineEmits(['update:open']);

const isOpen = ref(props.open ?? false);
const triggerRef = ref(null);
const contentRef = ref(null);

watch(
    () => props.open,
    (newVal) => {
        if (newVal !== undefined) {
            isOpen.value = newVal;
        }
    }
);

watch(isOpen, (newVal) => {
    emit('update:open', newVal);
    if (newVal) {
        nextTick(() => {
            updatePosition();
        });
    }
});

const updatePosition = () => {
    if (!triggerRef.value || !contentRef.value) return;

    const triggerRect = triggerRef.value.getBoundingClientRect();
    const contentRect = contentRef.value.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    let top = 0;
    let left = 0;

    switch (props.placement) {
        case 'right': {
            top = Math.min(Math.max(0, triggerRect.top), viewportHeight - contentRect.height);

            const rightSpace = viewportWidth - triggerRect.right - props.offset;
            if (rightSpace >= contentRect.width) {
                left = triggerRect.right + props.offset;
            } else {
                left = Math.max(props.offset, triggerRect.left - contentRect.width - props.offset);
            }
            break;
        }

        case 'left': {
            top = Math.min(Math.max(0, triggerRect.top), viewportHeight - contentRect.height);

            if (triggerRect.left > contentRect.width + props.offset) {
                left = triggerRect.left - contentRect.width - props.offset;
            } else {
                left = triggerRect.right + props.offset;
            }
            break;
        }

        case 'bottom': {
            left = Math.min(Math.max(props.offset, triggerRect.left + (triggerRect.width - contentRect.width) / 2), viewportWidth - contentRect.width - props.offset);

            const bottomSpace = viewportHeight - triggerRect.bottom - props.offset;
            if (bottomSpace >= contentRect.height) {
                top = triggerRect.bottom + props.offset;
            } else {
                top = Math.max(props.offset, triggerRect.top - contentRect.height - props.offset);
            }
            break;
        }

        case 'top': {
            left = Math.min(Math.max(props.offset, triggerRect.left + (triggerRect.width - contentRect.width) / 2), viewportWidth - contentRect.width - props.offset);

            if (triggerRect.top > contentRect.height + props.offset) {
                top = triggerRect.top - contentRect.height - props.offset;
            } else {
                top = Math.min(triggerRect.bottom + props.offset, viewportHeight - contentRect.height - props.offset);
            }
            break;
        }
    }

    Object.assign(contentRef.value.style, {
        position: 'fixed',
        top: `${top}px`,
        left: `${left}px`,
        zIndex: '1000'
    });
};

const handleResize = () => {
    if (isOpen.value) {
        updatePosition();
    }
};

const handleClickOutside = (event) => {
    if (isOpen.value && contentRef.value && !contentRef.value.contains(event.target) && !triggerRef.value.contains(event.target)) {
        isOpen.value = false;
        emit('update:open', false);
    }
};

onMounted(() => {
    window.addEventListener('resize', handleResize);
    document.addEventListener('mousedown', handleClickOutside);
});

onBeforeUnmount(() => {
    window.removeEventListener('resize', handleResize);
    document.removeEventListener('mousedown', handleClickOutside);
});

const toggle = () => {
    isOpen.value = !isOpen.value;
    emit('update:open', isOpen.value);
};

const handleClick = (event) => {
    event.stopPropagation();
    toggle();
};
</script>

<template>
    <div :data-popover-open="isOpen" class="group/sidebar-popover">
        <div ref="triggerRef" @click="handleClick" class="cursor-pointer">
            <slot name="trigger" :open="isOpen" :toggle="toggle" />
        </div>

        <Teleport to="body">
            <Transition
                enter-active-class="transition duration-200 ease-out"
                enter-from-class="opacity-0 scale-95"
                enter-to-class="opacity-100 scale-100"
                leave-active-class="transition duration-150 ease-in"
                leave-from-class="opacity-100 scale-100"
                leave-to-class="opacity-0 scale-95"
            >
                <div v-if="isOpen" ref="contentRef" class="will-change-transform w-48 bg-primary-600 dark:bg-surface-900 rounded-xl shadow-lg border border-primary-500 dark:border-surface-700 p-1 origin-top-left max-h-72 overflow-auto">
                    <slot name="content" :open="isOpen" :toggle="toggle" />
                </div>
            </Transition>
        </Teleport>
    </div>
</template>
